import React, { forwardRef, useCallback, useContext, useMemo, useRef, useState } from "react"
import { viewContext } from "../../src/context/viewContext"

const ChipDropdown = ({ choiceChipData, hovered, userList, maxOptionsDisplayed, isExpanded, handleExpand }) => {
  const displayedOptions = isExpanded ? userList : userList?.slice(0, maxOptionsDisplayed)

  return (
    hovered && (
      <div className="user-choice-chip-dropdown dome-absolute dome-scrollable" style={{boxShadow: hovered ? 'var(--light-shadow)' : 'none'}}>
        {displayedOptions?.map((user) => (
          <div key={user.id} className="user-info">
            <div className="dome-p3">{user.full_name}</div>
            <div className="dome-p4 dome-color-med-grey">{user.label || user.role}</div>
          </div>
        ))}
        {userList?.length > maxOptionsDisplayed && (
          <div
            key={`${choiceChipData.value}--show-more`}
            className="dome-color-aqua dome-text-w500"
            style={{ cursor: 'default' }}
            onClick={handleExpand}
          >
            {`show ${!isExpanded ? 'more' : 'less'}`}
          </div>
        )}
      </div>
    )
  )
}

export const ChoiceChip = forwardRef(({ choiceChipData, userList, customLabel, classes, onClick, prependedIcon, appendedIcon, isLast, alwaysShowIcon = false, maxOptionsDisplayed = 5 }, ref) => {
  const { unique_owner_id } = useContext(viewContext)
  const [hovered, setHovered] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showTextTooltip, setShowTextTooltip] = useState(false)
  const textRef = useRef(null)
  
  const selectorClasses = ['recipient-choice-chip-selector', 'dome-d-flex', 'dome-rounded-border']
  const containerClasses = ['recipient-choice-chip-container',  'dome-relative']
  selectorClasses.push(choiceChipData.user || !userList?.length ? 'no-dropdown' : '')
  selectorClasses.push(hovered ? 'dome-light-shadow hovered' : '')

  const mergedClasses = [
    ...selectorClasses,
    ...(Array.isArray(classes) ? classes : classes ? classes.split(' ') : [])
  ]

  const handleMouseEnter = () => {
    if (choiceChipData?.user || !userList?.length) return

    setHovered(true)
  }
  const handleMouseLeave = () => {
    setHovered(false)
    setShowTextTooltip(false)
  }
  const handleTextMouseEnter = () => {
    const el = textRef.current
    if (el && el.scrollHeight > el.clientHeight) {
      setShowTextTooltip(true)
    }
  }
  const handleTextMouseLeave = () => setShowTextTooltip(false)
  const handleExpand = () => setIsExpanded(!isExpanded)
  const handleClick = useCallback((item) => {
    if (!onClick) return

    onClick(item)
    setHovered(false)
    setIsExpanded(false)
  }, [onClick])

  const defaultLabel = useMemo(() => (
    <div className="default-label dome-d-flex dome-align-center dome-gap3">
      {(alwaysShowIcon || hovered) && prependedIcon}
      <span
        ref={textRef}
        className="label-ellipsis"
        onMouseEnter={handleTextMouseEnter}
        onMouseLeave={handleTextMouseLeave}
      >
        {choiceChipData.label} {!choiceChipData.role && userList?.length > 0 && <>({userList?.length})</>}
      </span>
      {(alwaysShowIcon || hovered) && appendedIcon}
    </div>
  ), [choiceChipData, userList, alwaysShowIcon, hovered, prependedIcon, appendedIcon])

  return (
    <div ref={ref} className={containerClasses.join(' ')} key={choiceChipData.value} onMouseLeave={handleMouseLeave}>
      <div
        className={mergedClasses.join(' ')}
        onClick={() => handleClick(choiceChipData)}
        onMouseEnter={handleMouseEnter}
      >
        {customLabel ?? defaultLabel}
      </div>
      {showTextTooltip && (
        <div className="tooltip-dropdown dome-bg-white dome-rounded-border dome-dark-grey-border dome-padding6 dome-color-darkest-grey dome-opaque">
          {choiceChipData.label}
        </div>
      )}

      <ChipDropdown
        choiceChipData={choiceChipData}
        hovered={hovered}
        userList={userList}
        maxOptionsDisplayed={maxOptionsDisplayed}
        isExpanded={isExpanded}
        handleExpand={handleExpand}
      />
    </div>
  )
})

ChoiceChip.displayName = 'ChoiceChip'
